import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { RotateCcw, Activity } from 'lucide-react';
import { toast } from 'sonner';
import CodeFirstTab from '../components/playground/CodeFirstTab';
import CrudTab from '../components/playground/CrudTab';
import LinqTab from '../components/playground/LinqTab';
import DbFirstTab from '../components/playground/DbFirstTab';
import LivePreviewPanel from '../components/playground/LivePreviewPanel';
import { sampleStudents } from '../data/mockData';

const PlaygroundPage = () => {
  const [students, setStudents] = useState(sampleStudents);
  const [tracking, setTracking] = useState(true);
  const [changes, setChanges] = useState({ added: 0, modified: 0, deleted: 0 });
  const [sqlOutput, setSqlOutput] = useState('-- SQL queries will appear here');
  const [migrations, setMigrations] = useState([]);
  const [entities, setEntities] = useState([]);
  const [csharpOutput, setCsharpOutput] = useState('// C# example will appear here');
  const [dbContextOutput, setDbContextOutput] = useState('// DbContext snapshot will appear here');

  const handleReset = () => {
    setStudents(sampleStudents);
    setTracking(true);
    setChanges({ added: 0, modified: 0, deleted: 0 });
    setMigrations([]);
    setEntities([]);
    toast.success('Playground reset successfully');
    setSqlOutput('-- SQL queries will appear here');
    setCsharpOutput('// C# example will appear here');
    setDbContextOutput('// DbContext snapshot will appear here');
  };

  const playgroundState = {
    students,
    setStudents,
    tracking,
    setTracking,
    changes,
    setChanges,
    sqlOutput,
    setSqlOutput,
    migrations,
    setMigrations,
    entities,
    setEntities,
    csharpOutput,
    setCsharpOutput,
    dbContextOutput,
    setDbContextOutput
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">EF Core Playground</h1>
              <p className="mt-2 text-muted-foreground">
                Interact with Entity Framework Core operations in real-time
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="lg">
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset Demo
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Playground?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear all your changes and reset the playground to its initial state.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset}>Reset</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left Column: Actions Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-border">
              <CardHeader>
                <CardTitle>EF Core Actions</CardTitle>
                <CardDescription>
                  Perform Entity Framework operations and see results in real-time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="code-first" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4">
                    <TabsTrigger onClick={() => {
                      setSqlOutput('-- SQL queries will appear here');
                      setCsharpOutput('// C# example will appear here');
                      setDbContextOutput('// DbContext snapshot will appear here');
                    }} value="code-first">Code-First</TabsTrigger>
                    <TabsTrigger onClick={() => {
                      setSqlOutput('-- SQL queries will appear here');
                      setCsharpOutput('// C# example will appear here');
                      setDbContextOutput('// DbContext snapshot will appear here');
                    }} value="db-first">Database-First</TabsTrigger>
                    <TabsTrigger onClick={() => {
                      setSqlOutput('-- SQL queries will appear here');
                      setCsharpOutput('// C# example will appear here');
                      setDbContextOutput('// DbContext snapshot will appear here');
                    }} value="crud">CRUD</TabsTrigger>
                    <TabsTrigger onClick={() => {
                      setSqlOutput('-- SQL queries will appear here');
                      setCsharpOutput('// C# example will appear here');
                      setDbContextOutput('// DbContext snapshot will appear here');
                    }} value="linq">LINQ</TabsTrigger>
                  </TabsList>
                  <TabsContent value="code-first">
                    <CodeFirstTab state={playgroundState} />
                  </TabsContent>
                  <TabsContent value="db-first">
                    <DbFirstTab state={playgroundState} />
                  </TabsContent>
                  <TabsContent value="crud">
                    <CrudTab state={playgroundState} />
                  </TabsContent>
                  <TabsContent value="linq">
                    <LinqTab state={playgroundState} />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: Live Preview */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <LivePreviewPanel state={playgroundState} />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PlaygroundPage;
